import React, { useState, useEffect } from 'react';
import { Dropdown,SplitButton, ButtonGroup, Button, Spinner} from 'react-bootstrap';
import { markPaymentReceiptAsPayedV2,printReceiptById } from '../../../../../../../../../crud/payments_api/payment_receipts';
import { getAllPaymentTypesByLocalId } from '../../../../../../../../../crud/payments_api/payment_types';
import { FaCheck,  FaTriangleExclamation, FaPrint , FaQrcode, FaCreditCard, FaPenToSquare } from 'react-icons/fa6';
import {getWorkstationsAvailableForPrinting} from '../../../../../../../../../crud/management_api/workstations';
import './ActionSection.scss';
import { toast } from 'react-toastify';
import { usePedidos } from '../../../../../../PedidosContext';
import InsufficientPayment from './components/InsufficientPayment/InsufficientPayment';
import { setClientCouponValidation } from '../../../../../../../../../crud/payments_api/client_coupons';
import QRModal from './components/QRModal/QRModal';
import MultiplePaymentsModal from './components/MultiplePaymentsModal/MultiplePaymentsModal';
import CashPaymentModal from '../CashPaymentModal/CashPaymentModal';
import { formatCurrency, getPermissionState } from '../../../../../../../../../utils/functions';

interface ActionSectionProps {
  receiptId: any;
  receipt: any;
  receipts: any[];
  setReceipts: any;
  payedFull: boolean;
  setPayedFull: (payedFull: boolean) => void;
  setCurrentIndex: (index: number) => void;
  selectedPaymentMethod: any;
  setSelectedPaymentMethod: (method: any) => void;
  couponCode?: string;
  externalSetPedidos?: (pedidos: any) => void;
  acceptChange?: boolean; // NEW: if true, show cash payment modal for cash payments
  onClose?: (targetSelections?: string[]) => void; // NEW: callback to close modal when all receipts are paid
  targetSelections?: string[]; // NEW: array of selection_ids for partial payments
}

interface PaymentMethod {
  payment_type_id: number;
  payment_type_name: string;
  is_custom: boolean;
  custom_payment_type_id?: number;
}

interface PaymentEntry {
  id: number;
  payment_method_id: number | null;
  payment_method_name: string;
  amount: number;
  custom_payment_type_id?: number;
  tip_amount?: number;
}

const ActionSection: React.FC<ActionSectionProps> = ({ 
  receiptId,
  receipt ,
  receipts, 
  setCurrentIndex, 
  setReceipts, 
  payedFull, 
  setPayedFull, 
  selectedPaymentMethod, 
  couponCode,
  setSelectedPaymentMethod,
  externalSetPedidos,
  acceptChange = false, // Default to false for backwards compatibility
  onClose, // NEW: callback to close modal
  targetSelections, // NEW: array of selection_ids for partial payments
}) => {
  const {pedidos,setPedidos} = usePedidos();
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false); // Separate loading state for payment
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [showInsufficientModal, setShowInsufficientModal] = useState(false);
  const [workstations, setWorkstations] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTerminal, setSelectedTerminal] = useState<any | null>(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showMultiplePaymentsModal, setShowMultiplePaymentsModal] = useState(false);
  const [multiplePayments, setMultiplePayments] = useState<PaymentEntry[]>([]);
  const [isMultiplePaymentMode, setIsMultiplePaymentMode] = useState(false);
  const [hasPaymentPermission, setHasPaymentPermission] = useState(false);
  const [hasCashChangePermission, setHasCashChangePermission] = useState(false);
  const [showCashPaymentModal, setShowCashPaymentModal] = useState(false);
  const [cashPaymentAmount, setCashPaymentAmount] = useState<number>(0); // Amount for cash payment modal (can be partial in multiple payments)
  const [pendingMultiplePaymentWithCash, setPendingMultiplePaymentWithCash] = useState(false); // Track if we're waiting for cash modal in multiple payments

  // ========== PERMISSIONS ==========
  useEffect(() => {
    const hasPermission = getPermissionState(130);
    setHasPaymentPermission(hasPermission);
    
    const hasCashChange = getPermissionState(134);
    setHasCashChangePermission(hasCashChange);
  }, []);


  useEffect(() => {
    setLoading(true);
    const user = JSON.parse(localStorage.getItem('user') ?? '{}');
    const localId = user.local_id ? Number(user.local_id) : 0;
    getAllPaymentTypesByLocalId(localId).then((response) => {
      setPaymentMethods(response.data);
      setLoading(false);
    }).catch((error) => {
      console.error('Error getting payment methods: ', error);
    });

    getWorkstationsAvailableForPrinting(localId).then((response) => {
      setWorkstations(response.data);
    }).catch((error) => {
      console.error('Error getting workstations: ', error);
    }
    ).finally(() => {
      setLoading(false);
    }
    );
  }, []);

  const handleMarkAsPayed = async () => {
    // Check if we should show cash payment modal
    // For single payment: show if it's cash AND (acceptChange OR hasCashChangePermission)
    // For multiple payments: show if ANY payment is cash AND (acceptChange OR hasCashChangePermission)
    const shouldRequireCashValidation = acceptChange || hasCashChangePermission;
    
    if (shouldRequireCashValidation) {
      if (!isMultiplePaymentMode && selectedPaymentMethod?.payment_type_id === 1) {
        // Single cash payment
        setCashPaymentAmount(getTotalDue());
        setPendingMultiplePaymentWithCash(false);
        setShowCashPaymentModal(true);
        return;
      }
      
      if (isMultiplePaymentMode && multiplePayments.length > 0) {
        // Check if any payment method is cash (payment_type_id === 1)
        const cashPayment = multiplePayments.find(p => p.payment_method_id === 1);
        if (cashPayment) {
          // Calculate total cash amount (amount + tip for cash payment)
          const cashAmount = cashPayment.amount + (cashPayment.tip_amount || 0);
          setCashPaymentAmount(cashAmount);
          setPendingMultiplePaymentWithCash(true);
          setShowCashPaymentModal(true);
          return;
        }
      }
    }

    // No cash payment or no validation required - proceed directly
    await processPayment();
  };

  // Extracted payment processing logic to be reusable
  const processPayment = async (cashPaymentData?: { receivedAmount: number; change: number; workstationId: number }) => {
    setPaymentLoading(true);
    const user = JSON.parse(localStorage.getItem('user') ?? '{}');
    const userId = user.user_id ? Number(user.user_id) : 0;
    
    try {
      // Prepare payment data based on payment mode
      let paymentData;
      let customPaymentTypeId = null;
      
      if (isMultiplePaymentMode && multiplePayments.length > 0) {
        // Multiple payments mode
        paymentData = {
          payment_type_id: null, // Set to null for multiple payments
          multiple_payment_methods: multiplePayments.map(payment => ({
            payment_type_id: payment.payment_method_id ?? 0,
            amount: payment.amount,
            tip_amount: payment.tip_amount ?? 0,
            custom_payment_type_id: payment.custom_payment_type_id ?? undefined,
            // Include cash payment data for the cash portion
            ...(payment.payment_method_id === 1 && cashPaymentData ? {
              cash_received: cashPaymentData.receivedAmount,
              cash_change: cashPaymentData.change,
              workstation_id: cashPaymentData.workstationId
            } : {})
          }))
        };
      } else {
        // Single payment mode
        // Check if selected payment method is custom
        if (selectedPaymentMethod?.is_custom && selectedPaymentMethod?.custom_payment_type_id) {
          customPaymentTypeId = selectedPaymentMethod.custom_payment_type_id;
        }
        
        paymentData = {
          payment_type_id: selectedPaymentMethod?.payment_type_id ?? 0,
          multiple_payment_methods: undefined
        };
      }
      
      // For single cash payments, pass change and workstationId directly
      // For multiple payments with cash, the data is included in the multiple_payment_methods array
      const singleCashChange = !isMultiplePaymentMode && cashPaymentData ? cashPaymentData.change : undefined;
      const singleWorkstationId = !isMultiplePaymentMode && cashPaymentData ? cashPaymentData.workstationId : undefined;
      // If user has cash change permission, always send approve_cash = true
      const approveCash = hasCashChangePermission ? true : undefined;
      
      await markPaymentReceiptAsPayedV2(
        receiptId, 
        userId, 
        null, 
        paymentData.payment_type_id, 
        'parallel',
        paymentData.multiple_payment_methods,
        customPaymentTypeId,
        singleCashChange,
        singleWorkstationId,
        approveCash
      );
      
      setReceipts((prev:any) =>
        prev.map((r:any) =>
          r.receipt_id === receiptId
            ? { ...r, payed_full: true }
            : r
        )
      );
      
      // Check if all receipts are now paid and close modal if so
      const updatedReceiptsList = receipts.map((r:any) =>
        r.receipt_id === receiptId ? { ...r, payed_full: true } : r
      );
      const allPaid = updatedReceiptsList.every((r: any) => r.payed_full);
      
      // look for the pedido with pedido.receipt_id === receiptId , and set pedido.payed = true
      const updatedPedidos = pedidos.map((pedido) => {
        if (pedido.receipt_id === receiptId) {
          return { ...pedido, payed: true };
        }
        return pedido;
      });
      if (receipt.pedido_type_id !== 4) {
        // Use external setPedidos if provided, otherwise use context
        if (externalSetPedidos) {
          externalSetPedidos(updatedPedidos);
        } else {
          setPedidos(updatedPedidos);
        }
      }
      // --- INICIO CAMBIO CUPON ---
      // Si hay cupón válido, invalidarlo (usa prop couponCode si existe y hay monto, si no usa el del recibo)
      if (couponCode && couponCode !== '' && receipt.coupon_amount > 0) {
        setClientCouponValidation(couponCode, false);
      } else if (receipt.coupon_code && receipt.coupon_code !== '' && receipt.coupon_amount > 0) {
        setClientCouponValidation(receipt.coupon_code, false);
      }
      // --- FIN CAMBIO CUPON ---
      setPayedFull(true);
      toast.success('Recibo marcado como pagado');
      
      console.log('[ActionSection] Payment completed. Receipt:', receipt);
      console.log('[ActionSection] All paid:', allPaid, 'Has onClose:', !!onClose, 'Mesa ID:', receipt.mesa_id);
      
      // If all receipts are paid, dispatch event to update mesa state in real-time
      // BUT ONLY for FULL payments - partial payments should keep mesa visually active
      const isPartialPayment = targetSelections && targetSelections.length > 0;
      if (allPaid && receipt.mesa_id && !isPartialPayment) {
        console.log('[ActionSection] Dispatching mesa-payment-completed event for mesa:', receipt.mesa_id);
        window.dispatchEvent(new CustomEvent('mesa-payment-completed', { 
          detail: { mesa_id: receipt.mesa_id } 
        }));
      } else if (allPaid && receipt.mesa_id && isPartialPayment) {
        console.log('[ActionSection] Partial payment - NOT dispatching mesa-payment-completed event to keep mesa visually active');
      }
      
      // If all receipts are paid and modal should close, close it after a short delay
      if (allPaid && onClose) {
        setTimeout(() => {
          // Pass targetSelections to indicate this was a partial payment (if applicable)
          onClose(targetSelections);
        }, 1500); // 1.5 second delay to let user see success message
        return;
      }
      
      // If not all paid, navigate to next unpaid receipt
      if (receipts.length > 1) {
        const nextReceiptIndex = receipts.findIndex((receipt) => !receipt.payed_full && receipt.receipt_id !== receiptId);
        if (nextReceiptIndex !== -1 && nextReceiptIndex !== undefined && nextReceiptIndex !== null) {
          setTimeout(() => setCurrentIndex(nextReceiptIndex), 2000);
        } else {
          setCurrentIndex(0);
        }
      }
    } catch (error) {
      console.error('Error marking receipt as payed: ', error);
      toast.error('Error marcando recibo como pagado');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handlePrintReceipt = (workstationId: number | null) => {
    setIsLoading(true);
    printReceiptById(receiptId,workstationId ).then((response) => {
        toast.success('Recibo impreso correctamente');
    }).catch((error) => {
      console.error('Error printing receipt: ', error);
      toast.error('Error imprimiendo recibo');
    })
    .finally(() => {
      setIsLoading(false);
    });
  }

  const handlePrintAllReceipts = async (workstationId: number | null) => {
    if (receipts.length <= 1) {
      // If only one receipt, just print it normally
      handlePrintReceipt(workstationId);
      return;
    }

    setIsLoading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const r of receipts) {
      try {
        await printReceiptById(r.receipt_id, workstationId);
        successCount++;
      } catch (error) {
        console.error(`Error printing receipt ${r.receipt_id}: `, error);
        errorCount++;
      }
    }

    setIsLoading(false);

    if (errorCount === 0) {
      toast.success(`${successCount} recibos impresos correctamente`);
    } else if (successCount === 0) {
      toast.error('Error imprimiendo los recibos');
    } else {
      toast.warning(`${successCount} recibos impresos, ${errorCount} con errores`);
    }
  };

  const handleMultiplePaymentsConfirm = (payments: PaymentEntry[]) => {
    setMultiplePayments(payments);
    setIsMultiplePaymentMode(true);
    setSelectedPaymentMethod(null); // Clear single payment method
  };

  const handleSelectMultiplePayments = () => {
    setShowMultiplePaymentsModal(true);
  };

  const handleEditMultiplePayments = () => {
    setShowMultiplePaymentsModal(true);
  };

  const getTotalDue = () => {
    return Math.max(0, (receipt.subtotal - receipt.coupon_amount - receipt.discount_amount) + receipt.suggested_tip - receipt.total_payed);
  };

  const handleCashPaymentConfirm = async (data: { receivedAmount: number; change: number; workstationId: number }) => {
    setShowCashPaymentModal(false);
    // Use the unified processPayment function with cash data
    await processPayment(data);
    // Reset the pending flag
    setPendingMultiplePaymentWithCash(false);
  };

  const getPaymentDisplayText = () => {
    if (isMultiplePaymentMode && multiplePayments.length > 0) {
      return `${multiplePayments.length} métodos (${formatCurrency(multiplePayments.reduce((sum, p) => sum + p.amount, 0))})`;
    }
    return selectedPaymentMethod ? selectedPaymentMethod.payment_type_name : 'Seleccionar método';
  };


  const renderPaymentMethods = () => {
    return (
      <div className='individual-receipt-footer-actions'>
        {/* Grupo de botones principales centrados */}
        <div className="main-action-buttons">
          {hasPaymentPermission && (
            <Dropdown>
                <Dropdown.Toggle className='mp-button Credit' variant='none'>
                    <FaCreditCard />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    {paymentMethods.map(method => (
                        <Dropdown.Item key={method.payment_type_id} onClick={() => {
                          setSelectedPaymentMethod(method);
                          setIsMultiplePaymentMode(false);
                          setMultiplePayments([]);
                        }}>
                            {method.payment_type_name}
                        </Dropdown.Item>
                    ))}
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={handleSelectMultiplePayments}>
                        <strong>Múltiples</strong>
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>
          )}

          <Button className='mp-button icon-only'
            variant='none'
            onClick={() => setShowQRModal(true)}>
              <FaQrcode/>
          </Button>

          <SplitButton
            as={ButtonGroup}
            id="print-split-button"
            variant="none"
            title={isLoading ? "..." : <FaPrint />}
            onClick={() => handlePrintReceipt(selectedTerminal?.workstation_id ?? null)}
            className="print-button-custom icon-only"
            disabled={isLoading}
          >
            {/* Print All option - only show when multiple receipts */}
            {receipts.length > 1 && (
              <>
                <Dropdown.Item 
                  onClick={() => handlePrintAllReceipts(selectedTerminal?.workstation_id ?? null)}
                  className="print-all-option"
                >
                  <strong>Imprimir todos ({receipts.length})</strong>
                </Dropdown.Item>
                <Dropdown.Divider />
              </>
            )}
            {workstations.map(t => (
              <Dropdown.Item key={t.workstation_id ?? 'default'} 
                onClick={() => setSelectedTerminal(t)}>
                {t.workstation_name}
              </Dropdown.Item>
            ))}
          </SplitButton>
        </div>

        <QRModal
          show={showQRModal}
          onHide={() => setShowQRModal(false)}
          receiptId={receiptId}
        />

        <MultiplePaymentsModal
          show={showMultiplePaymentsModal}
          onHide={() => setShowMultiplePaymentsModal(false)}
          paymentMethods={paymentMethods}
          totalDue={getTotalDue()}
          onConfirm={handleMultiplePaymentsConfirm}
          initialPayments={multiplePayments}
          suggestedTip={receipt.suggested_tip || 0}
          receiptId={receiptId}
          receipts={receipts}
          setReceipts={setReceipts}
        />

        <CashPaymentModal
          show={showCashPaymentModal}
          onHide={() => setShowCashPaymentModal(false)}
          totalDue={cashPaymentAmount}
          onConfirm={handleCashPaymentConfirm}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className='individual-receipt-footer-actions'>
        <div className="loading-placeholder" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '1rem',
          color: 'var(--text-color-muted)',
          fontSize: '0.9rem'
        }}>
          Cargando métodos de pago...
        </div>
      </div>
    );
  }

  return (
    <>
    {/* Only show payment methods when payment is not complete */}
    {!payedFull && !loading && renderPaymentMethods()}
      {hasPaymentPermission && (selectedPaymentMethod || isMultiplePaymentMode) && !payedFull && (
        <div className="individual-receipt-footer-actions">
          {/* Main Payment Button */}
          <Button
            variant="none"
            onClick={() => handleMarkAsPayed()}
            className="individual-receipt-footer-button"
            disabled={paymentLoading}
          >
            {paymentLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  style={{ marginRight: '0.5rem' }}
                />
                Procesando...
              </>
            ) : (
              <>
                <FaCheck style={{ marginRight: '0.5rem' }} />
                Pagado ({getPaymentDisplayText()})
                {isMultiplePaymentMode && (
                  <button
                    className="edit-payments-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditMultiplePayments();
                    }}
                    title="Editar métodos de pago"
                    disabled={paymentLoading}
                  >
                    <FaPenToSquare />
                  </button>
                )}
              </>
            )}
          </Button>

          {/* Small insufficient payment link below main button - only for single receipts */}
          {receipts?.length <= 1 && (
            <div className="insufficient-payment-link-container">
              <button 
                className="insufficient-payment-link"
                onClick={() => setShowInsufficientModal(true)}
                disabled={paymentLoading}
              >
                <FaTriangleExclamation /> Reportar pago insuficiente
              </button>
            </div>
          )}
          
          {/* Modal approach for insufficient payment */}
          <InsufficientPayment
            show={showInsufficientModal}
            receiptId={receiptId}
            receipt={receipt}
            receipts={receipts}
            setReceipts={setReceipts}
            setCurrentIndex={setCurrentIndex}
            onClose={() => {
              setShowInsufficientModal(false);
            }}
            selectedPaymentMethod={selectedPaymentMethod}
          />
        </div>
      )}
      {/* Show completed payment confirmation */}
      {hasPaymentPermission && payedFull && (selectedPaymentMethod || isMultiplePaymentMode) && (
        <div className="individual-receipt-footer-actions">
          <div className="individual-receipt-footer-button" style={{
            background: 'linear-gradient(135deg, #28a745 0%, #34ce57 100%)',
            borderRadius: '16px',
            color: 'white',
            fontWeight: '600',
            fontSize: '1rem',
            padding: '0.75rem 1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            width: '80%',
            maxWidth: '350px',
            minWidth: '240px',
            boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)',
            cursor: 'default'
          }}>
            <FaCheck style={{ marginRight: '0.5rem' }} />
            PAGADO - {getPaymentDisplayText()}
          </div>
        </div>
      )}
    </>
  );
};

export default ActionSection;
